import React from 'react';
import { Store } from 'redux';
import { Provider } from 'react-redux';
import GoldenLayout from 'golden-layout';
import { WidgetType } from 'widgets/WidgetType';
import MultipleInput from './MultipleInput';
import { AppContextProvider } from 'app/AppContext';
import Services from 'store/Services';
import ErrorBoundary from 'utils/components/ErrorBoundary';
import Widget from 'widgets/Widget';
import { WidgetComponentProps } from 'widgets/WidgetComponentProps';
import DOMPurify from 'dompurify';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface RootRenderedComponentProps {
    id: string;
    width: number;
    height: number;
    onSetTitle?: (title: string) => void;
}

export interface RootComponentExposedProps {
    componentId: string;
    componentType: WidgetType;
    store: Store;
    services: Services;
}

export interface RootComponentProps extends RootComponentExposedProps {
    glContainer: {
        on: (eventName: string, cb: () => void) => void;
        off: (eventName: string, cb: () => void) => void;
        layoutManager: any;
        parent: any;
        width: number;
        height: number;
        setTitle: (title: string) => void;
    };
}

interface RootComponentState {
    width: number;
    height: number;
    showMultipleInput: boolean;
}

export default function createRootComponentClass(
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Component: React.FC<WidgetComponentProps>
) {
    return class RootComponent extends React.Component<
        RootComponentProps,
        RootComponentState
    > {
        private setTitleEx: (title: string) => void;

        public constructor(props: RootComponentProps) {
            super(props);

            const { width, height, setTitle } = this.props.glContainer;
            this.state = {
                width,
                height,
                showMultipleInput: false,
            };
            this.setTitleEx = setTitle.bind(this.props.glContainer);
            this.onSetTitle = this.onSetTitle.bind(this);
        }

        public render() {
            const { componentId, componentType, store, services } = this.props;
            const { width, height, showMultipleInput } = this.state;
            return (
                <Provider store={store}>
                    <AppContextProvider services={services}>
                        <ErrorBoundary
                            componentId={componentId}
                            componentType={componentType}
                            services={services}
                        >
                            {showMultipleInput ? (
                                <MultipleInput
                                    componentType={componentType}
                                    placeInTab={this.placeInTab}
                                    remove={this.remove}
                                    widgetKey={componentId}
                                    widgetType={componentType}
                                />
                            ) : (
                                <Widget
                                    component={Component}
                                    host={'layout'}
                                    widgetKey={componentId}
                                    widgetType={componentType}
                                    width={width}
                                    height={height}
                                    onSetTitle={this.onSetTitle}
                                />
                            )}
                        </ErrorBoundary>
                    </AppContextProvider>
                </Provider>
            );
        }

        public shouldComponentUpdate(
            prevProps: RootComponentProps,
            prevState: RootComponentState
        ) {
            return (
                this.state.width !== prevState.width ||
                this.state.height !== prevState.height ||
                this.state.showMultipleInput !== prevState.showMultipleInput
            );
        }

        public componentDidMount() {
            this.props.glContainer.on('resize', this.handleResize);
            this.props.glContainer.layoutManager.on(
                'stateChanged',
                this.handleManagerStateChanged
            );
        }

        public componentWillUnmount() {
            this.props.glContainer.off('resize', this.handleResize);
            this.props.glContainer.layoutManager.off(
                'stateChanged',
                this.handleManagerStateChanged
            );
        }

        private onSetTitle(nextTitle: string) {
            if (this.setTitleEx) {
                this.setTitleEx(DOMPurify.sanitize(nextTitle));
            }
        }

        private handleResize = () => {
            const { width, height } = this.props.glContainer;
            this.setState({ width, height });
        };

        private handleManagerStateChanged = () => {
            const { componentId, componentType, services } = this.props;
            const { widgetsService } = services;

            if (widgetsService.isInput(componentType)) {
                const { activeInput } = this.props.glContainer.layoutManager;
                const activeInputId = activeInput
                    ? (activeInput.config as GoldenLayout.ReactComponentConfig)
                          .props.componentId
                    : undefined;

                if (activeInputId !== componentId) {
                    this.setState({ showMultipleInput: true });
                } else {
                    this.setState({ showMultipleInput: false });
                }
            }
        };

        private placeInTab = () => {
            const activeInput =
                this.props.glContainer.layoutManager.activeInput;
            if (!activeInput) {
                return;
            }

            this.remove();

            let item: any = activeInput;
            while (
                item.config.type === 'component' ||
                item.config.type === 'react-component'
            ) {
                item = item.parent;
            }

            if (item.addChild) {
                item.addChild(this.props.glContainer.parent.config);
            }
        };

        private remove = () => {
            let item: any = this.props.glContainer;
            while (!item.contentItems || item.contentItems.length === 0) {
                item = item.parent;
            }

            if (item.removeChild) {
                item.removeChild((this.props.glContainer as any).parent);
            }
        };
    };
}
